import { useCallback, useEffect, useRef, useState } from "react";
import { getConnection } from "../utils/signalRConnection";

const MAX_SIGNALR_RETRIES = 10;

const useSignalR = () => {
	const [connection, setConnection] = useState(null);
	const retryCountRef = useRef(0);
	const retryTimerRef = useRef(null);
	const onConnectedCallbacks = useRef([]);

	const onConnected = useCallback((cb) => {
		onConnectedCallbacks.current.push(cb);
		if (connection?.state === "Connected") {
			cb(connection);
		}
	}, [connection]);

	// Handler registry: [{ type, group, callback }]
	const handlers = useRef([]);

	// Register business-logic handlers
	const registerHandler = useCallback((type, group, callback) => {
		const handler = { type, group, callback };
		handlers.current.push(handler);

		return () => {
			handlers.current = handlers.current.filter(h => h !== handler);
		};
	}, []);

	useEffect(() => {
		const location = window.location.origin + "/hubs/signalr";
		const conn = getConnection(location);
		setConnection(conn);

		const startConnection = async () => {
			try {
				if (conn.state !== "Disconnected") {
					console.debug("SignalR connection not in Disconnected state:", conn.state);
					return;
				}

				await conn.start();
				retryCountRef.current = 0;
				console.debug("SignalR connected:", location, conn.state);
				onConnectedCallbacks.current.forEach(cb => cb(conn));
			}
			catch (err) {
				console.error("SignalR connection error details:", err, conn.state);
				++retryCountRef.current;
				if (retryCountRef.current > MAX_SIGNALR_RETRIES) {
					console.error(`SignalR connection failed after ${MAX_SIGNALR_RETRIES} attempts`, err);
					return;
				}

				const delay = Math.min(30000, 2000 * (2 ** (retryCountRef.current - 1)));
				console.warn(`SignalR reconnect attempt ${retryCountRef.current} failed — retrying in ${delay / 1000}s`);

				clearTimeout(retryTimerRef.current);
				retryTimerRef.current = setTimeout(startConnection, delay);
			}
		};

		conn.onclose(() => {
			if (conn.state === "Disconnected") {
				startConnection();
			}
		});

		if (conn.state === "Disconnected") {
			startConnection();
		}

		return () => {
			clearTimeout(retryTimerRef.current);
		};
	}, []);

	// Unified message dispatcher
	useEffect(() => {
		if (!connection) {
			return;
		}

		const onMessage = (message) => {
			console.debug("Recieved Message from SignalR server...", message);
			handlers.current.forEach((h) => {
				if (h.type === message.type && h.group === message.group) {
					h.callback(message);
				}
			});
		};

		connection.on("ReceiveMessageAsync", onMessage);

		return () => {
			connection.off("ReceiveMessageAsync", onMessage);
		};
	}, [connection]);

	return {
		connection,
		registerHandler,
		onConnected
	};
};

export default useSignalR;